import styled, { css } from "styled-components";
import {
  BORDER_COLORS,
  COLORS,
  ICON_COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS } from "../../constants/design/fontSize";
import { BREAKPOINTS } from "../../constants/design/media";
import {
  BORDER_RADIUS,
  COMPONENTS,
  SPACING,
} from "../../constants/design/spacing";
import { foregroundComponent } from "../../style";
import { glow } from "../Buttons/styles";
import { fadeInCSS } from "../shared/style";

export const ButtonWrapper = styled.button<{ isActive?: boolean }>`
  ${foregroundComponent}

  width: ${COMPONENTS.subHeaderButtonSize}px;
  aspect-ratio: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: ${BORDER_RADIUS.five}px;

  svg {
    width: 20px;
    height: 20px;
    transition: stroke 0.2s ease-in-out;
    stroke: ${ICON_COLORS.three};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      svg {
        stroke: ${COLORS.highlight.one};
      }
      box-shadow: 0px 0px 0px 1px ${COLORS.highlight.one} inset;
    `}

  &:hover {
    svg {
      stroke: ${COLORS.highlight.one};
    }
  }

  ${glow(COLORS.highlight.one)}
`;

export const TradingHeaderWrapper = styled.div<{ $isMobileScreen?: boolean }>`
  ${fadeInCSS}
  z-index: 3;
  position: relative;
  display: flex;
  align-items: center;
  height: ${COMPONENTS.header.sub}px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  background: ${LAYER_COLORS.one};
  border-radius: ${BORDER_RADIUS.five}px;
  overflow: hidden;
  margin-bottom: ${({ $isMobileScreen }) =>
    $isMobileScreen ? SPACING.two : 0}px;

  ${ButtonWrapper} {
    padding: initial;
  }

  > *:not(:last-child) {
    margin-right: ${SPACING.three}px;
  }
`;

export const PriceOnlyLabel = styled.span<{ $color?: string }>`
  ${FONT_CSS.data.two}
  color: ${({ $color }) => $color};
`;

export const SpotChartWrapper = styled.div`
  display: flex;
  width: ${COMPONENTS.optionsSpotChartWrapperWidth}px;
`;

export const SpotWrapper = styled.div`
  flex: 1;

  > *:not(:last-child) {
    margin-bottom: ${SPACING.one}px;
  }
`;

export const PriceWrapper = styled.div`
  display: flex;
`;

export const PriceChartWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

/* Label shown above each stat value */
export const StatLabel = styled.span`
  ${FONT_CSS.label.three}
  color: ${TEXT_COLORS.two};
  white-space: nowrap;
`;

export const StatContainer = styled.div<{ $interactive?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 32px;
  position: relative;

  &:first-of-type {
    padding-left: ${SPACING.three}px;
  }

  /* Tighten the generous desktop gutters on mobile so more stats fit before
     the strip needs horizontal scrolling. */
  ${BREAKPOINTS.mobile} {
    padding: 0 ${SPACING.three}px;

    &:first-of-type {
      padding-left: ${SPACING.two}px;
    }
  }

  &:not(:last-of-type)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 20px;
    width: 1px;
    background: ${BORDER_COLORS.two};
  }
`;

export const StatLabelRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
`;

export const InfoIconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: help;
  border-radius: 50%;
  color: ${ICON_COLORS.three};
  transition: color 0.15s ease;

  /* Expand the touch target to ~32px without enlarging the 12px icon or
     shifting the compact stat-strip layout. */
  &::before {
    content: "";
    position: absolute;
    inset: -9px;
  }

  &:hover {
    color: ${ICON_COLORS.one};

    svg {
      color: ${ICON_COLORS.one};
    }
  }
`;

export const OtherStatsWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  min-width: max-content;
`;

export const StatData = styled.div<{ $color?: string }>`
  ${FONT_CSS.body.four}
  color: ${({ $color }) => $color ?? TEXT_COLORS.one};
  font-feature-settings: "tnum";
`;

export const OpenInterestTooltip = styled.div`
  min-width: 190px;
`;

export const OpenInterestTooltipRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${SPACING.two}px;

  &:not(:last-child) {
    margin-bottom: ${SPACING.two}px;
  }

  span {
    ${FONT_CSS.body.five}
  }

  strong {
    ${FONT_CSS.data.four}
    text-align: right;
  }

  small {
    ${FONT_CSS.body.six}
    grid-column: 1 / -1;
    opacity: 0.7;
    text-align: right;
  }
`;

export const BuySellVolumeContainer = styled.div`
  ${FONT_CSS.data.four}
  display: flex;
  align-items: center;

  > span {
    &:first-child {
      margin-right: ${SPACING.two}px;
    }

    &:last-child {
      margin-left: ${SPACING.two}px;
    }
  }

  > div {
    width: 1px;
    height: 8px;
    border-radius: 8px;

    &:not(:last-child) {
      margin-right: 1px;
    }
  }
`;
